import { useId } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { CreateFooter } from '../create/CreateFooter';
import { ServiceTypePicker, offeredServiceTypes, pickedFrom } from './ServiceTypePicker';

/**
 * THE step that asks for a service type and then sends a write with it (ops insights Phase 1):
 * the shared picker over the shared `CreateFooter`. Every page that asks for a type outside the
 * new-job sheet mounts this one step, so all of them ask, refuse and wait the same way:
 *
 *   - a conversion creating a job: "Schedule the work" on an approved quote (ticket 04) and
 *     "Approve → Job" on a request (05). A quote or request carries no type of its own, so the
 *     office picks it here, and the job is created only once one is picked. The defaults
 *     ("Create job", focus on open) are theirs.
 *   - giving EXISTING jobs a type (08): the Jobs list's "Set service type" on the jobs ticked,
 *     and the job page's change on one (`SetServiceType.tsx` holds what both share).
 *
 * Controlled, like the picker: the page holds the chosen id (so opening the step again can ask
 * again) and sends its write from `onSubmit`, which is handed the type to send. A type that is
 * no longer on offer (turned off or deleted since it was picked, or the list not loaded) counts
 * as not picked, by the new-job sheet's rule (`pickedFrom`), so it is never sent: a refused
 * write refetches the list (`refetchServiceTypes`), and the type it named drops out. There is no
 * "none" to pick: a job's type is never cleared.
 */
export function ServiceTypeStep({
  value,
  onChange,
  pending,
  locked = false,
  label,
  pendingLabel,
  missing,
  autoFocus = true,
  picker,
  cancelLabel,
  cancelOff,
  onSubmit,
  onCancel,
}: {
  value: string;
  onChange: (serviceTypeId: string) => void;
  /** The step's write is on its way: the button says so, and neither button does anything. */
  pending: boolean;
  /**
   * Another write on the same record is on its way: the button does nothing until it lands
   * (`CreateFooter`). On the quote page that is "Create invoice": sent beside it, the job
   * could land after the invoice and never be on it.
   */
  locked?: boolean;
  /** What the button says: "Create job" unless the write is something else ("Save"). */
  label?: string;
  /** What it says while the write is on its way ("Creating…" unless given). */
  pendingLabel?: string;
  /**
   * What ELSE the write still needs, asked before the type (the Jobs list's jobs, before any
   * are ticked). The type itself is added when none on offer is picked.
   */
  missing?: readonly string[];
  /**
   * Focus the picker as it appears: yes where a press opened the step (the default), no where
   * the step is always on screen and focus belongs elsewhere (the Jobs list's boxes).
   */
  autoFocus?: boolean;
  /** The picker's own words for this page (`ServiceTypePicker`'s `label`, `emptyReason`). */
  picker?: { label?: string; emptyReason?: string };
  /** "Cancel", "Back", "Clear selection": whatever the page calls leaving without sending. */
  cancelLabel: string;
  /** Nothing to leave or clear yet (nothing ticked): the way out is off too. */
  cancelOff?: boolean;
  onSubmit: (serviceTypeId: string) => void;
  onCancel: () => void;
}) {
  const trpc = useTRPC();
  // The picker's own list (one cache entry, no second fetch): the press checks the value
  // against what the picker offers now.
  const serviceTypes = useQuery(trpc.serviceType.list.queryOptions());
  const reasonId = useId();
  return (
    <div className="space-y-3">
      <ServiceTypePicker
        value={value}
        onChange={onChange}
        autoFocus={autoFocus}
        label={picker?.label}
        emptyReason={picker?.emptyReason}
      />
      <ServiceTypeStepActions
        picked={pickedFrom(offeredServiceTypes(serviceTypes.data ?? []), value)}
        pending={pending}
        locked={locked}
        label={label}
        pendingLabel={pendingLabel}
        missing={missing}
        reasonId={reasonId}
        cancelLabel={cancelLabel}
        cancelOff={cancelOff}
        onSubmit={onSubmit}
        onCancel={onCancel}
      />
    </div>
  );
}

/**
 * The step's foot: the shared `CreateFooter`, fed from what the step holds. `picked` is the type
 * on offer that the step holds, or '' for none, and the press hands exactly that to `onSubmit`.
 * No hooks, so a test can call it, and the footer it returns, and press the real buttons.
 */
export function ServiceTypeStepActions({
  picked,
  pending,
  locked = false,
  label = 'Create job',
  pendingLabel,
  missing = [],
  reasonId,
  cancelLabel,
  cancelOff = false,
  onSubmit,
  onCancel,
}: {
  picked: string;
  pending: boolean;
  locked?: boolean;
  label?: string;
  pendingLabel?: string;
  missing?: readonly string[];
  reasonId: string;
  cancelLabel: string;
  cancelOff?: boolean;
  onSubmit: (serviceTypeId: string) => void;
  onCancel: () => void;
}) {
  return (
    <CreateFooter
      label={label}
      pendingLabel={pendingLabel}
      missing={[...missing, ...(picked ? [] : ['a service type'])]}
      pending={pending}
      locked={locked}
      reasonId={reasonId}
      onSubmit={() => onSubmit(picked)}
      cancel={{ label: cancelLabel, onCancel, off: cancelOff }}
    />
  );
}
